import { Link } from "@tanstack/react-router"
import { Separator } from "design-system/atoms/Separator"
import { formatNumber, formatPercent } from "lopermut"
import { ArrowUpRight } from "lucide-react"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import React from "react"
import { useTranslation } from "react-i18next"
import { SegmentGroupResponseDataSchema } from "schema"
import z from "zod"

export type SegmentGroupTooltipData = {
  payload: {
    id: string
    name: string
    value: number
    hasOverlap: boolean
    base?: number
    totalPercent?: number
    translatedOverlappingSegments?: Array<{
      id: string
      translatedName: string
      overlapCount: number
      overlapPercent?: number
      color?: string
    }>
  }
  coordinate: { x: number; y: number }
}

type Props = {
  segmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>
  tooltip: SegmentGroupTooltipData
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export const SegmentGroupChartTooltipOverlay = ({
  segmentGroup,
  tooltip,
  onMouseEnter,
  onMouseLeave,
}: Props) => {
  const { t } = useTranslation()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()

  const ref = React.useRef<HTMLDivElement | null>(null)
  const [size, setSize] = React.useState({ width: 0, height: 0 })

  React.useLayoutEffect(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect()
      if (size.width === r.width && size.height === r.height) return

      setSize({ width: r.width, height: r.height })
    }
  }, [size])

  const viewportPadding = 0
  const verticalCursorOffset = 0
  const horizontalCursorOffset = 0
  const vw = typeof window !== "undefined" ? window.innerWidth : 0

  // Position to the top right of the mouse coordinate (absolute positioning)
  let left = tooltip.coordinate.x + horizontalCursorOffset
  let top = tooltip.coordinate.y - size.height - verticalCursorOffset

  // Prevent vertical cutoff (if above goes off-screen, position below)
  if (top < viewportPadding) {
    top = tooltip.coordinate.y + verticalCursorOffset
  }

  // Prevent horizontal cutoff (if right goes off-screen, position to the left)
  if (left + size.width > vw - viewportPadding) {
    left = tooltip.coordinate.x - size.width - horizontalCursorOffset
  }

  // Prevent vertical cutoff (if above goes off-screen, position below)
  if (top < viewportPadding) {
    top = tooltip.coordinate.y + verticalCursorOffset
  }

  // Prevent horizontal cutoff (if right goes off-screen, position to the left)
  if (left + size.width > vw - viewportPadding) {
    left = tooltip.coordinate.x - size.width - horizontalCursorOffset
  }

  return (
    <div
      ref={ref}
      style={{ position: "fixed", left, top }}
      className="rounded-md bg-card border border-border p-4 shadow-xl sm:min-w-xs min-w-full max-w-[min(32rem,100vw)] z-50 select-none"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-base text-foreground font-medium">
            {getSegmentGroupTranslation(segmentGroup)}
          </span>
          <Link
            className="flex flex-row items-center justify-between gap-4 hover:text-accent-foreground hover:underline"
            to="/customers/list"
            search={{
              filterBy: { segmentIds: [tooltip.payload.id] },
              field: "lastMessageSentAt",
              order: "desc",
              page: 1,
            }}
          >
            <span className="text-sm text-muted-foreground font-medium">
              {tooltip.payload.name}
            </span>
            <div className="flex flex-row items-center gap-1">
              <span className="text-sm text-foreground font-medium tabular-nums">
                {t("segments.distribution.customersCount", {
                  count: tooltip.payload.value,
                  formattedCount: formatNumber({ amount: tooltip.payload.value }),
                })}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums font-mono">
                {t("segments.distribution.percentage", {
                  percentage: formatPercent({ amount: tooltip.payload.totalPercent, fix: 1 }, true),
                })}
              </span>
              <ArrowUpRight className="text-muted-foreground size-4" />
            </div>
          </Link>
        </div>
        {tooltip.payload.hasOverlap &&
          tooltip.payload.translatedOverlappingSegments &&
          tooltip.payload.translatedOverlappingSegments.length > 0 && (
            <>
              <Separator />
              <div className="flex flex-col gap-1">
                <div className="text-xs text-muted-foreground">
                  {t("segments.distribution.overlapping")}
                </div>
                {tooltip.payload.translatedOverlappingSegments.map((overlap) => {
                  const isUnknown = overlap.id === "-1"

                  const content = (
                    <>
                      <div className="flex items-center gap-2">
                        <div
                          className="size-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: overlap.color }}
                        />
                        <span className="text-muted-foreground">{overlap.translatedName}</span>
                      </div>
                      <div className="flex flex-row items-center gap-1">
                        <span className="text-foreground tabular-nums">
                          {t("segments.distribution.customersCount", {
                            count: overlap.overlapCount,
                            formattedCount: formatNumber({ amount: overlap.overlapCount }),
                          })}
                        </span>
                        <span className="text-muted-foreground tabular-nums font-mono">
                          {t("segments.distribution.percentage", {
                            percentage: formatPercent(
                              { amount: overlap.overlapPercent, fix: 1 },
                              true,
                            ),
                          })}
                        </span>
                        {!isUnknown ? (
                          <ArrowUpRight className="text-muted-foreground size-4" />
                        ) : (
                          <div className="size-4" />
                        )}
                      </div>
                    </>
                  )

                  if (isUnknown) {
                    return (
                      <div
                        key={overlap.id}
                        className="flex justify-between items-center text-xs gap-2"
                      >
                        {content}
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={overlap.id}
                      className="flex justify-between items-center text-xs gap-2 hover:text-accent-foreground hover:underline"
                      to="/customers/list"
                      search={{
                        filterBy: { segmentIds: [tooltip.payload.id, overlap.id] },
                        field: "lastMessageSentAt",
                        order: "desc",
                        page: 1,
                      }}
                    >
                      {content}
                    </Link>
                  )
                })}
              </div>
            </>
          )}
      </div>
    </div>
  )
}
